import PageHeader from "@/src/components/ui-kit/page-header";
import { AddCircle } from "iconsax-react-nativejs";
import { useCallback, useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  LayoutChangeEvent,
  Text,
  View,
} from "react-native";
import { useTranslation } from "react-i18next";
import { useRouter } from "expo-router";
import GroupCard, { GroupCardProps } from "@/src/components/groups/group-card";
import GroupCardSkeleton from "@/src/components/groups/group-card-skeleton";
import GroupInfoSidebar from "@/src/components/groups/group-info-sidebar";
import ScToast from "@/src/components/ui-kit/toast";
import { useGroups } from "@/src/hooks/features/groups/use-group";
import { usePermissions } from "@/src/hooks/use-permissions";
import PermissionDenied from "@/src/components/ui-kit/permission-denied";
import StateDisplay from "@/src/components/ui-kit/state-display";
import { useResponsiveGap } from "@/src/hooks/use-responsive-gap";

const GAP = 12;
const CONTAINER_PADDING = 4;

const getColumns = (width: number): number => {
  if (width >= 700) return 3;
  if (width >= 400) return 2;
  return 1;
};

const getCardWidth = (containerWidth: number, columns: number): number =>
  (containerWidth - GAP * (columns - 1)) / columns;

const Groups = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const {
    searchText,
    setSearchText,
    tabs,
    activeTab,
    handleTabChange,
    toast,
    setToast,
    filtered,
    isPending,
    error,
    isFetchingNextPage,
    loadMore,
    latestGroups,
    handleViewGroup,
  } = useGroups();

  const gap = useResponsiveGap();
  const { canCreate, canView } = usePermissions();
  const canCreateGroup = canCreate("group");
  const canViewGroup = canView("group");

  const [columns, setColumns] = useState(1);
  const [cardWidth, setCardWidth] = useState(0);

  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    const width = e.nativeEvent.layout.width - CONTAINER_PADDING;
    const cols = getColumns(width);
    setColumns(cols);
    setCardWidth(getCardWidth(width, cols));
  }, []);

  const renderItem = ({ item }: { item: GroupCardProps }) => (
    <View
      style={
        columns > 1
          ? cardWidth > 0
            ? { width: cardWidth }
            : { flex: 1 }
          : { width: "100%" }
      }
    >
      <GroupCard
        {...item}
        singleColumn={columns === 1}
        onViewPress={() => handleViewGroup(item.ItemId)}
      />
    </View>
  );

  if (!canViewGroup) {
    return (
      <View className={`flex-1 flex-row w-full bg-obsidian ${gap}`}>
        <PermissionDenied />
      </View>
    );
  }

  return (
    <View
      className={`flex-1 flex-row w-full bg-obsidian ${gap}`}
    >
      {toast && (
        <View className="absolute top-0 left-0 right-64 z-50 items-center">
          <ScToast
            text1={toast.text1}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        </View>
      )}
      <View className="w-full md:w-[68%] flex-col gap-4">
        <PageHeader
          searchText={searchText}
          onSearchChange={setSearchText}
          tabs={tabs}
          active={t(activeTab)}
          onTabChange={handleTabChange}
          searchPlaceholderKey={t("LABEL_SEARCH_GROUPS")}
          actionText={t("BTN_CREATE_GROUP")}
          actionIcon={<AddCircle size={20} color="white" />}
          onActionPress={
            canCreateGroup ? () => router.push("/groups/create") : undefined
          }
        />
        <View className="flex-1 md:bg-chalk md:p-4 md:border md:border-ash-3 md:rounded-xl">
          {isPending ? (
            <View onLayout={handleLayout} className="flex-1">
              <View
                className="flex-row flex-wrap"
                style={{ gap: GAP }}
              >
                {Array.from({ length: 6 }).map((_, index) => (
                  <View
                    key={index}
                    style={{ width: cardWidth > 0 ? cardWidth : "100%" }}
                  >
                    <GroupCardSkeleton />
                  </View>
                ))}
              </View>
            </View>
          ) : error ? (
            <Text className="text-red-500">{error.message}</Text>
          ) : (
            <FlatList
              onLayout={handleLayout}
              key={`${columns}-${activeTab}`}
              data={filtered}
              keyExtractor={(item) => item.ItemId}
              renderItem={renderItem}
              showsVerticalScrollIndicator={false}
              numColumns={columns}
              columnWrapperStyle={columns > 1 ? { gap: GAP } : undefined}
              contentContainerStyle={{ gap: GAP, flexGrow: 1 }}
              ListEmptyComponent={
                <View className="flex-1 items-center justify-center">
                  <StateDisplay
                    descriptionLabel="LABEL_NO_GROUPS"
                    imgSource={require("@/src/assets/images/empty.png")}
                  />
                </View>
              }
              ListFooterComponent={
                isFetchingNextPage ? (
                  <ActivityIndicator className="py-4" />
                ) : null
              }
              onEndReached={loadMore}
              onEndReachedThreshold={0.3}
            />
          )}
        </View>
      </View>

      <View className="hidden md:flex md:w-[26%]">
        <GroupInfoSidebar
          latestGroups={latestGroups}
          onSelectGroup={handleViewGroup}
        />
      </View>
    </View>
  );
};

export default Groups;
